import React, { useEffect, useState } from 'react';
import { projectsApi } from '../api/client';
import type { Projects as ProjectsType, ActiveProject, PastProject } from '../api/client';
import './Projects.css';

const Projects: React.FC = () => {
  const [projects, setProjects] = useState<ProjectsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    type: 'active' as 'active' | 'past',
    technologies: '',
    status: '',
    priority: 'medium' as 'high' | 'medium' | 'low',
    category: '',
    startDate: '',
    targetDate: '',
    progress: 0,
    repository: '',
    completedDate: '',
    duration: '',
    outcome: ''
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await projectsApi.getProjects();
      setProjects(response.data);
    } catch (err) {
      setError('Failed to load projects');
      console.error('Projects error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProject = async () => {
    if (!newProject.name.trim() || !newProject.description.trim()) return;

    try {
      const projectData = {
        name: newProject.name,
        description: newProject.description,
        type: newProject.type,
        technologies: newProject.technologies.split(',').map(t => t.trim()).filter(t => t),
        category: newProject.category || 'General',
        ...(newProject.type === 'active' && {
          priority: newProject.priority,
          status: newProject.status || 'Planning',
          startDate: newProject.startDate,
          targetDate: newProject.targetDate,
          progress: newProject.progress,
          repository: newProject.repository
        }),
        ...(newProject.type === 'past' && {
          status: newProject.status || 'Completed',
          completedDate: newProject.completedDate,
          duration: newProject.duration,
          outcome: newProject.outcome
        })
      };

      const response = await projectsApi.addProject(projectData);
      setProjects(response.data);
      setShowAddModal(false);
      resetNewProject();
    } catch (err) {
      console.error('Error adding project:', err);
      setError('Failed to add project');
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      await projectsApi.deleteProject(projectId);
      await fetchProjects(); // Refresh projects after deletion
    } catch (err) {
      console.error('Error deleting project:', err);
      setError('Failed to delete project');
    }
  };

  const resetNewProject = () => {
    setNewProject({
      name: '',
      description: '',
      type: 'active',
      technologies: '',
      status: '',
      priority: 'medium',
      category: '',
      startDate: '',
      targetDate: '',
      progress: 0,
      repository: '',
      completedDate: '',
      duration: '',
      outcome: ''
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed': return '#27ae60';
      case 'in development': return '#3498db';
      case 'active': return '#2ecc71';
      case 'maintenance': return '#f39c12';
      case 'planning': return '#9b59b6';
      case 'archived': return '#95a5a6';
      default: return '#34495e';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#e74c3c';
      case 'medium': return '#f39c12';
      default: return '#95a5a6';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'web development': return '🌐';
      case 'web application': return '💻';
      case 'mobile development': return '📱';
      case 'game development': return '🎮';
      case 'api development': return '🔌';
      case 'development tools': return '🛠️';
      case 'frontend framework': return '⚛️';
      default: return '📁';
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return '#27ae60';
    if (progress >= 50) return '#f39c12';
    if (progress >= 20) return '#3498db';
    return '#95a5a6';
  };

  const filteredActiveProjects = projects?.activeProjects?.filter(project => {
    const statusMatch = filterStatus === 'all' || project.status.toLowerCase() === filterStatus.toLowerCase();
    const categoryMatch = filterCategory === 'all' || project.category.toLowerCase() === filterCategory.toLowerCase();
    return statusMatch && categoryMatch;
  }) || [];

  const filteredPastProjects = projects?.pastProjects?.filter(project => {
    const statusMatch = filterStatus === 'all' || project.status.toLowerCase() === filterStatus.toLowerCase();
    const categoryMatch = filterCategory === 'all' || project.category.toLowerCase() === filterCategory.toLowerCase();
    return statusMatch && categoryMatch;
  }) || [];

  const renderActiveProject = (project: ActiveProject) => (
    <div key={project.id} className="project-card active">
      <div className="project-header">
        <div className="project-title-section">
          <span className="category-icon">{getCategoryIcon(project.category)}</span>
          <h3 className="project-title">{project.name}</h3>
        </div>
        <div className="project-badges">
          <span 
            className="priority-badge"
            style={{ backgroundColor: getPriorityColor(project.priority) }}
          >
            {project.priority}
          </span>
          <span 
            className="status-badge"
            style={{ backgroundColor: getStatusColor(project.status) }}
          >
            {project.status}
          </span>
        </div>
        <button 
          className="delete-btn"
          onClick={() => handleDeleteProject(project.id)}
          title="Delete project"
        >
          ×
        </button>
      </div>

      <div className="project-content">
        <p className="project-description">{project.description}</p>
        
        <div className="project-details">
          <div className="detail-row">
            <span className="detail-label">Category:</span>
            <span className="detail-value">{project.category}</span>
          </div>
          
          {project.progress !== undefined && (
            <div className="detail-row">
              <span className="detail-label">Progress:</span>
              <div className="progress-container">
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ 
                      width: `${project.progress}%`,
                      backgroundColor: getProgressColor(project.progress)
                    }}
                  />
                </div>
                <span className="progress-text">{project.progress}%</span>
              </div>
            </div>
          )}

          {project.startDate && (
            <div className="detail-row">
              <span className="detail-label">Started:</span>
              <span className="detail-value">{new Date(project.startDate).toLocaleDateString()}</span>
            </div>
          )}

          {project.targetDate && (
            <div className="detail-row">
              <span className="detail-label">Target:</span>
              <span className="detail-value">{new Date(project.targetDate).toLocaleDateString()}</span>
            </div>
          )}

          {project.repository && (
            <div className="detail-row">
              <span className="detail-label">Repository:</span>
              <a 
                href={project.repository} 
                target="_blank" 
                rel="noopener noreferrer"
                className="repo-link"
              >
                View Code
              </a>
            </div>
          )}
        </div>

        {project.technologies && project.technologies.length > 0 && (
          <div className="technologies">
            {project.technologies.map((tech, index) => (
              <span key={index} className="tech-tag">{tech}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderPastProject = (project: PastProject) => (
    <div key={project.id} className="project-card past">
      <div className="project-header">
        <div className="project-title-section">
          <span className="category-icon">{getCategoryIcon(project.category)}</span>
          <h3 className="project-title">{project.name}</h3>
        </div>
        <div className="project-badges">
          <span 
            className="status-badge completed"
            style={{ backgroundColor: getStatusColor(project.status) }}
          >
            {project.status}
          </span>
        </div>
        <button 
          className="delete-btn"
          onClick={() => handleDeleteProject(project.id)}
          title="Delete project"
        >
          ×
        </button>
      </div>

      <div className="project-content">
        <p className="project-description">{project.description}</p>
        
        <div className="project-details">
          <div className="detail-row">
            <span className="detail-label">Category:</span>
            <span className="detail-value">{project.category}</span>
          </div>

          {project.completedDate && (
            <div className="detail-row">
              <span className="detail-label">Completed:</span>
              <span className="detail-value">{new Date(project.completedDate).toLocaleDateString()}</span>
            </div>
          )}

          {project.duration && (
            <div className="detail-row">
              <span className="detail-label">Duration:</span>
              <span className="detail-value">{project.duration}</span>
            </div>
          )}

          {project.outcome && (
            <div className="detail-row">
              <span className="detail-label">Outcome:</span>
              <span className="detail-value">{project.outcome}</span>
            </div>
          )}
        </div>

        {project.technologies && project.technologies.length > 0 && (
          <div className="technologies">
            {project.technologies.map((tech, index) => (
              <span key={index} className="tech-tag past">{tech}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return <div className="projects-page"><div className="loading">Loading projects...</div></div>;
  }

  if (error) {
    return <div className="projects-page"><div className="error">Error: {error}</div></div>;
  }

  const uniqueStatuses = Array.from(new Set([
    ...(projects?.activeProjects?.map(p => p.status) || []),
    ...(projects?.pastProjects?.map(p => p.status) || [])
  ]));

  const uniqueCategories = Array.from(new Set([
    ...(projects?.activeProjects?.map(p => p.category) || []),
    ...(projects?.pastProjects?.map(p => p.category) || [])
  ]));

  return (
    <div className="projects-page">
      <header className="page-header">
        <h1>💼 Projects</h1>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowAddModal(true)}
        >
          Add New Project
        </button>
      </header>

      {error && <div className="error-message">{error}</div>}

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Filter by Status:</label>
          <select 
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Statuses</option>
            {uniqueStatuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Filter by Category:</label>
          <select 
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Categories</option>
            {uniqueCategories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>

        <div className="filter-stats">
          <span>{filteredActiveProjects.length} active</span>
          <span>{filteredPastProjects.length} past</span>
        </div>
      </div>

      <div className="projects-content">
        {/* Active Projects */}
        <div className="projects-section">
          <div className="section-header">
            <h2>Active Projects</h2>
            <span className="project-count">{filteredActiveProjects.length}</span>
          </div>
          <div className="projects-grid">
            {filteredActiveProjects.map(renderActiveProject)}
            {filteredActiveProjects.length === 0 && (
              <div className="no-projects">No active projects found</div>
            )}
          </div>
        </div>

        {/* Past Projects */}
        <div className="projects-section">
          <div className="section-header">
            <h2>Past Projects</h2>
            <span className="project-count">{filteredPastProjects.length}</span>
          </div>
          <div className="projects-grid">
            {filteredPastProjects.map(renderPastProject)}
            {filteredPastProjects.length === 0 && (
              <div className="no-projects">No past projects found</div>
            )}
          </div>
        </div>
      </div>

      {/* Add Project Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Project</h3>
              <button 
                className="modal-close"
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Project Type</label>
                  <select 
                    value={newProject.type}
                    onChange={(e) => setNewProject({...newProject, type: e.target.value as 'active' | 'past'})}
                    className="form-select"
                  >
                    <option value="active">Active Project</option>
                    <option value="past">Past Project</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={newProject.category}
                    onChange={(e) => setNewProject({...newProject, category: e.target.value})}
                    className="form-select"
                  >
                    <option value="">Select Category</option>
                    <option value="Web Development">Web Development</option>
                    <option value="Mobile Development">Mobile Development</option>
                    <option value="Game Development">Game Development</option>
                    <option value="API Development">API Development</option>
                    <option value="Development Tools">Development Tools</option>
                    <option value="Frontend Framework">Frontend Framework</option>
                    <option value="General">General</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Project Name</label>
                <input
                  type="text"
                  value={newProject.name}
                  onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                  className="form-input"
                  placeholder="Enter project name..."
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newProject.description}
                  onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                  className="form-textarea"
                  rows={3}
                  placeholder="Describe the project..."
                />
              </div>

              <div className="form-group">
                <label>Technologies (comma-separated)</label>
                <input
                  type="text"
                  value={newProject.technologies}
                  onChange={(e) => setNewProject({...newProject, technologies: e.target.value})}
                  className="form-input"
                  placeholder="React, Node.js, MongoDB..."
                />
              </div>

              {newProject.type === 'active' ? (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Priority</label>
                      <select 
                        value={newProject.priority}
                        onChange={(e) => setNewProject({...newProject, priority: e.target.value as 'high' | 'medium' | 'low'})}
                        className="form-select"
                      >
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Status</label>
                      <select
                        value={newProject.status}
                        onChange={(e) => setNewProject({...newProject, status: e.target.value})}
                        className="form-select"
                      >
                        <option value="">Select Status</option>
                        <option value="Planning">Planning</option>
                        <option value="In Development">In Development</option>
                        <option value="Active">Active</option>
                        <option value="Maintenance">Maintenance</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Start Date</label>
                      <input
                        type="date"
                        value={newProject.startDate}
                        onChange={(e) => setNewProject({...newProject, startDate: e.target.value})}
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label>Target Date</label>
                      <input
                        type="date"
                        value={newProject.targetDate}
                        onChange={(e) => setNewProject({...newProject, targetDate: e.target.value})}
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Progress (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={newProject.progress}
                        onChange={(e) => setNewProject({...newProject, progress: parseInt(e.target.value) || 0})}
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label>Repository URL</label>
                      <input
                        type="url"
                        value={newProject.repository}
                        onChange={(e) => setNewProject({...newProject, repository: e.target.value})}
                        className="form-input"
                        placeholder="https://github.com/..."
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Status</label>
                      <select
                        value={newProject.status}
                        onChange={(e) => setNewProject({...newProject, status: e.target.value})}
                        className="form-select"
                      >
                        <option value="">Select Status</option>
                        <option value="Completed">Completed</option>
                        <option value="Archived">Archived</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Completed Date</label>
                      <input
                        type="date"
                        value={newProject.completedDate}
                        onChange={(e) => setNewProject({...newProject, completedDate: e.target.value})}
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Duration</label>
                    <input
                      type="text"
                      value={newProject.duration}
                      onChange={(e) => setNewProject({...newProject, duration: e.target.value})}
                      className="form-input"
                      placeholder="e.g., 6 months, 2 weeks..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Outcome</label>
                    <textarea
                      value={newProject.outcome}
                      onChange={(e) => setNewProject({...newProject, outcome: e.target.value})}
                      className="form-textarea"
                      rows={2}
                      placeholder="Describe the project outcome..."
                    />
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button 
                className="btn btn-success"
                onClick={handleAddProject}
                disabled={!newProject.name.trim() || !newProject.description.trim()}
              >
                Add Project
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;